// "My pickup code": a site-signed pass bound to a holder key made on this phone, shown as a QR
// that re-signs every 30 s window (docs/spec/events.md §6). Once the pass is cached, it works
// with no network: the kiosk verifies both signatures offline.
import { exportPublicKey, generateKeyPair, presentPass, WINDOW_SECONDS } from "@vavr/core";
import type { Kv } from "../shared/kv.ts";

export interface CachedPass { pass: string; site_pubkey: string; not_after: number; fetched_at: number; /** Id of this phone's registered key; it also signs self-service handovers. */ key_id?: string }

/** Seconds until the QR rotates (30…1). */
export const secondsLeft = (nowMs: number) => WINDOW_SECONDS - (Math.floor(nowMs / 1000) % WINDOW_SECONDS);

/** Milliseconds until the next 30 s window starts, for the rotation timer. */
export const msToNextWindow = (nowMs: number) => WINDOW_SECONDS * 1000 - (nowMs % (WINDOW_SECONDS * 1000));

const DAY = 86_400_000;

/** Refresh when online if there is no pass, it is a day old (children may have changed), or it expires within a week. */
export function needsRefresh(c: CachedPass | undefined, nowMs: number): boolean {
  if (!c) return true;
  return nowMs - c.fetched_at > DAY || c.not_after * 1000 - nowMs < 7 * DAY;
}

/** Usable offline: present and not past not_after. */
export const usable = (c: CachedPass | undefined, nowMs: number): c is CachedPass => !!c && c.not_after * 1000 > nowMs;

/** The phone's holder key: P-256, private half non-extractable, kept in IndexedDB. */
export async function holderKey(kv: Kv): Promise<CryptoKeyPair> {
  const existing = await kv.get<CryptoKeyPair>("holder");
  if (existing?.privateKey) return existing;
  const keys = await generateKeyPair(false);
  await kv.set("holder", keys);
  return keys;
}

type FetchPass = (siteId: string, holderKeyB64: string) => Promise<{ pass: string; site_pubkey: string; not_after: number; key_id?: string }>;

/** Returns a pass for the site: the cached one, refreshed from the server when due and possible. */
export async function passFor(kv: Kv, siteId: string, fetchPass: FetchPass, nowMs = Date.now(), force = false): Promise<{ cached: CachedPass | null; refreshError: Error | null }> {
  const key = `pass:${siteId}`;
  let cached = await kv.get<CachedPass>(key);
  let refreshError: Error | null = null;
  if (force || needsRefresh(cached, nowMs)) {
    try {
      const keys = await holderKey(kv);
      const r = await fetchPass(siteId, await exportPublicKey(keys.publicKey));
      cached = { ...r, fetched_at: nowMs };
      await kv.set(key, cached);
    } catch (e) {
      refreshError = e as Error;
    }
  }
  return { cached: usable(cached, nowMs) ? cached : null, refreshError };
}

/** The QR text for this window. */
export async function qrText(kv: Kv, c: CachedPass, nowMs = Date.now()): Promise<string> {
  const keys = await holderKey(kv);
  return presentPass(c.pass, keys.privateKey, Math.floor(nowMs / 1000));
}

/** Sign-out forgets the key and every pass; a new sign-in makes a new key. */
export const forgetPasses = (kv: Kv) => kv.clear();
